import type Highcharts from "highcharts";
import React from "react";
import type { ActiveAnalysis } from "../../../../../../../../../api/useActiveAnalysesStream";
import type { Project } from "../../../../../../../../../api/useProjects";
import type { Setup } from "../../../../../../../../../api/useSetups";
import t from "../../../../../../../../../i18n";
import { useCanUserDoOnProject } from "../../../../../../../../canUserDo";
import useIdle from "../../../../../../../../../lib/hooks/useIdle";
import ChartCard from "@ui/Highcharts/ui/Card";
import Selector from "@ui/Selector";
import Switch from "@ui/Switch";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { IndexedFailureValues } from "../../../../../../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../../../../../../api/useManualFailureValues";
import { AnalysisDateTitle } from "../../../../../../../AnalysisDateTitle";
import type { IndexedFailureMetadata } from "../../../../../../../../../api/useFailureMetadata";
import Legend from "../../../../../../../Diagnosis/Symptoms/Legend";
import type { ObservableOrder } from "../../../../../../../Diagnosis/Symptoms/Observables/useOptions";
import ProcessIndicators from "./ProcessIndicators";
import Observables from "./Observables";
import { useDiagnosisFailureOrder } from "../../../../../../../Diagnosis/Symptoms/hook/useDiagnosisFailureOrder";
import { useShowInactiveObservables } from "../../../../../../../Diagnosis/Symptoms/hook/useShowInactiveObservables";
import { useFlagSelectedQuantities } from "../../../../../../../Diagnosis/Symptoms/hook/useCommonOptions";
import { useDisplayAllQuantities } from "../../../../../../hooks/createGlobalStates/useDisplayAllQuantities";
import { useRevelantQuantities } from "../../../../../../hooks/useRevelantQuantities";
import { RelevantQuantitiesSwitch } from "../../../../../../components/Switches/RelevantQuantitiesSwitch";
import { QuantitySelectorType } from "../../../../../../../QuantitySelector";
import { colors, fonts, spacings } from "@ui/Theme";
import Text, { H4 } from "@ui/Text";

import { css, cx } from "@linaria/core";
import AnalysisFilterCompliant from "../../../../../../../Analysis/AnalysisFilterCompliant";
import IndicatorsDisplay from "../../../../../../../Analysis/IndicatorDisplay";
import { type Quantity } from "../../../../../../../../../api/useQuantities";
import { InformationLine, SortAlphabetAsc, SortDesc } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { isAnalysisQuantityValid } from "../../../../../../../metrology";
import type { Event } from "../../../../../../../../../api/useEvents";

const orderOptionsBase: {
  label: string;
  value: ObservableOrder;
  description: string;
}[] = [
  {
    label: t("diagnosis.title.observables.order.decreasing.impact"),
    value: "DECREASING_IMPACT_ORDER",
    description: t("diagnosis.title.observables.order.decreasing.impact.description"),
  },
  {
    label: t("diagnosis.title.observables.order.alphabetical"),
    value: "ALPHABETICAL_ORDER",
    description: t("diagnosis.title.observables.order.alphabetical.description"),
  },
];

interface P {
  faultEventScope: string;
  event: Event;
  visibleSymptomHistogramChartId: string;
  project: Project;
  quantities: Quantity[];
  setups: Setup[];
  analysis: ActiveAnalysis;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
}

const DiagnosisSymptomAndImpacts = ({
  faultEventScope,
  event,
  visibleSymptomHistogramChartId,
  project,
  quantities,
  setups,
  analysis,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
}: P) => {
  const idle = useIdle(300);
  const canUpdateFailureValues = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const [order, setOrder] = useDiagnosisFailureOrder({ scope: project.id });

  const [showInactiveObservables, setShowInactiveObservables] = useShowInactiveObservables({
    scope: faultEventScope,
  });
  const [flagSelectedQuantities, setFlagSelectedQuantities] = useFlagSelectedQuantities({
    scope: project.id,
  });
  const [displayAllQuantities, setDisplayAllQuantities] = useDisplayAllQuantities({
    scope: faultEventScope,
  });
  React.useEffect(() => {
    if (!displayAllQuantities && showInactiveObservables) {
      setShowInactiveObservables(false);
    }
  }, [displayAllQuantities, showInactiveObservables, setShowInactiveObservables]);

  const observableChartRef = React.useRef<Highcharts.Chart>(null);

  const setup = React.useMemo(() => getAnalysisSetup(setups, analysis), [setups, analysis]);
  const revelantQuantities = useRevelantQuantities({
    dataType: QuantitySelectorType.Diagnosis,
    projectId: project.id,
    event,
    setups,
    quantities,
  });
  const hasInactiveObservables = React.useMemo(
    () =>
      setup &&
      setup.diagnosisInputs
        .filter(input => quantities.some(quantity => quantity.id === input.quantityId))
        .some(observable => observable.enabled === false),
    [quantities, setup],
  );

  const observableCount = React.useMemo(() => {
    const inputs = setup?.diagnosisInputs ?? [];
    const obsQuantities = inputs.filter(input => {
      const hasQuantity = quantities.some(q => q.id === input.quantityId);
      if (!hasQuantity) return false;
      return showInactiveObservables ? true : input.enabled;
    });
    const filtered =
      event.type === "MANUAL" || displayAllQuantities
        ? obsQuantities
        : obsQuantities.filter(input => revelantQuantities.some(r => r.id === input.quantityId));
    return filtered.length;
  }, [
    setup,
    quantities,
    showInactiveObservables,
    displayAllQuantities,
    event.type,
    revelantQuantities,
  ]);

  const orderOptionsWithIcons = React.useMemo(
    () =>
      orderOptionsBase.map((opt, i) => ({
        ...opt,
        label: "" as string,
        title: opt.label,
        tooltipText: opt.description,
        icon: i === 0 ? <SortDesc /> : <SortAlphabetAsc />,
      })),
    [],
  );

  const sortSelector = React.useMemo(
    () =>
      event.type === "DIAGNOSED" ? (
        <Selector options={orderOptionsWithIcons} value={order} onChange={setOrder} />
      ) : null,
    [order, setOrder, orderOptionsWithIcons, event.type],
  );

  const displayAllQuantitiesToggle = React.useMemo(
    () =>
      event.type === "DIAGNOSED" ? (
        <RelevantQuantitiesSwitch
          dataType={QuantitySelectorType.Diagnosis}
          checked={displayAllQuantities}
          onChange={() => setDisplayAllQuantities(s => !s)}
        />
      ) : null,
    [event.type, displayAllQuantities, setDisplayAllQuantities],
  );

  const processIndicators = React.useMemo(
    () =>
      quantities
        .filter(q => q.processIndicatorOrder !== null)
        .sort((a, b) => (a.processIndicatorOrder ?? 0) - (b.processIndicatorOrder ?? 0)),
    [quantities],
  );

  const indicatorQuantityValues = React.useMemo(() => {
    const validProcessIndicators = processIndicators.filter(quantity =>
      isAnalysisQuantityValid(analysis, quantity),
    );

    return Object.fromEntries(
      Object.entries(analysis.quantityValues).filter(([key]) =>
        validProcessIndicators.some(q => q.id === key),
      ),
    );
  }, [processIndicators, analysis]);

  const indicators = React.useMemo(
    () =>
      processIndicators.map(indicator => ({
        name: indicator.name,
        measured: indicatorQuantityValues[indicator.id]?.measured ?? null,
        unit: indicator.unit || "",
      })),
    [processIndicators, indicatorQuantityValues],
  );

  const observablesToggle = React.useMemo(() => {
    if (!hasInactiveObservables || !displayAllQuantities) return null;

    return (
      <div>
        <Switch
          className={styledSwitch}
          checked={showInactiveObservables}
          onChange={() => setShowInactiveObservables(v => !v)}
        >
          <Text>{t("diagnosis.chart.show_deactivated_observables")}</Text>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                className={styledOverlayWrapper}
                text={<div>{t("diagnosis.chart.show_deactivated_observables.tooltip")}</div>}
              />
            }
          >
            <span className={centerIcon}>
              <InformationLine />
            </span>
          </TooltipV2>
        </Switch>
      </div>
    );
  }, [
    hasInactiveObservables,
    displayAllQuantities,
    showInactiveObservables,
    setShowInactiveObservables,
  ]);

  const selectedQuantitiesToggle = React.useMemo(
    () => (
      <div>
        <Switch
          checked={flagSelectedQuantities}
          onChange={() => setFlagSelectedQuantities(v => !v)}
        >
          <Text>{t("diagnosis.chart.flag_selected_quantities")}</Text>
          <TooltipV2
            placement="top"
            overlay={
              <OverlayWrapper
                text={t("diagnosis.chart.flag_selected_quantities.tooltip.description")}
              />
            }
          >
            <span className={centerIcon}>
              <InformationLine />
            </span>
          </TooltipV2>
        </Switch>
      </div>
    ),
    [flagSelectedQuantities, setFlagSelectedQuantities],
  );

  const performanceIndicatorQuantities = React.useMemo(
    () => quantities.filter(q => q.qoiOrder !== null),
    [quantities],
  );

  const hasPI = performanceIndicatorQuantities.length > 0;
  const performanceIndicatorCount = performanceIndicatorQuantities.length;

  const legend = React.useMemo(
    () => <Legend project={project} analysis={analysis} />,
    [analysis, project],
  );

  const qoiTitle =
    performanceIndicatorCount === 0
      ? t("diagnosis.charts.symptoms.title.qoi")
      : performanceIndicatorCount === 1
        ? t("diagnosis.charts.symptoms.title.qoi.one")
        : t("diagnosis.charts.symptoms.title.qoi.many", { count: performanceIndicatorCount });
  const observablesTitle =
    observableCount === 0
      ? t("diagnosis.charts.symptoms.title.obs")
      : observableCount === 1
        ? t("diagnosis.charts.symptoms.title.obs.one")
        : t("diagnosis.charts.symptoms.title.obs.many", { count: observableCount });

  const children = React.useMemo(
    () => (
      <div className={charts}>
        {setup && (
          <div className={performanceIndicators}>
            {hasPI && <H4 className={cx(chartsTitle, performanceIndicatorsTitle)}>{qoiTitle}</H4>}
            <ProcessIndicators
              visibleSymptomHistogramChartId={visibleSymptomHistogramChartId}
              event={event}
              project={project}
              quantities={quantities}
              analysis={analysis}
              indexedFailureValues={indexedFailureValues}
              indexedFailureMetadata={indexedFailureMetadata}
              indexedManualFailureValues={indexedManualFailureValues}
              canUpdateFailureValues={canUpdateFailureValues}
              observableChartRef={observableChartRef}
              setups={setups}
              setup={setup}
            />
          </div>
        )}
        <div className={observables}>
          <div className={observablesHeader}>
            <H4 className={chartsTitle}>{observablesTitle}</H4>
            {sortSelector}
            {displayAllQuantitiesToggle}
            {observablesToggle}
            {selectedQuantitiesToggle}
          </div>
          <Observables
            visibleSymptomHistogramChartId={visibleSymptomHistogramChartId}
            event={event}
            project={project}
            setups={setups}
            analysis={analysis}
            indexedFailureMetadata={indexedFailureMetadata}
            indexedFailureValues={indexedFailureValues}
            indexedManualFailureValues={indexedManualFailureValues}
            quantities={quantities}
            order={order}
            observableChartRef={observableChartRef}
            showInactiveObservables={showInactiveObservables}
          />
        </div>
      </div>
    ),
    [
      setup,
      hasPI,
      qoiTitle,
      observablesTitle,
      sortSelector,
      displayAllQuantitiesToggle,
      observablesToggle,
      selectedQuantitiesToggle,
      visibleSymptomHistogramChartId,
      event,
      project,
      quantities,
      analysis,
      indexedFailureValues,
      indexedFailureMetadata,
      indexedManualFailureValues,
      canUpdateFailureValues,
      setups,
      order,
      showInactiveObservables,
    ],
  );

  // defer rendering of below-the-fold graph
  if (!idle) return null;

  return (
    <ChartCard
      title={
        <div className={chartCardTitle}>
          <AnalysisDateTitle event={event} analysis={analysis} timeZone={project.timezone} />
          <AnalysisFilterCompliant project={project} quantities={quantities} analysis={analysis} />
          <IndicatorsDisplay indicators={indicators} />
        </div>
      }
      legend={legend}
    >
      {children}
    </ChartCard>
  );
};

export default React.memo(DiagnosisSymptomAndImpacts);

// #region CSS
const charts = css`
  display: flex;
  align-items: flex-start;
`;

const observablesHeader = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  column-gap: 16px;
  row-gap: 8px;
  margin-bottom: ${spacings.xs};
`;
const chartCardTitle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const chartsTitle = css`
  color: ${colors.carbon900};
  margin-bottom: ${spacings.xs};
  font: ${fonts.body1SemiBold};
`;
const performanceIndicatorsTitle = css`
  grid-column: 2 / span 2;
  grid-row: 1 / 2;
  align-self: flex-start;
`;
const performanceIndicators = css`
  flex-grow: 1;
  flex-shrink: 0;

  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(2, 1fr);
  max-width: 40%;

  &:not(:has(.${performanceIndicatorsTitle})) {
    display: block;
  }

  > :not(.${performanceIndicatorsTitle}) {
    grid-row: 2;
  }
`;
const observables = css`
  width: 100%;
  overflow: hidden;

  .${performanceIndicators} + & {
    margin-left: ${spacings.m};
    padding-left: ${spacings.m};
    border-left: 1px solid ${colors.titanium200};
  }

  .${observablesHeader} .${chartsTitle} {
    margin-bottom: 0;
    align-self: center;
  }

  .${chartsTitle} {
    align-self: flex-start;
  }
`;
const styledSwitch = css`
  & > span {
    display: flex;
    align-items: center;
  }
`;
const centerIcon = css`
  padding-left: ${spacings.xxs};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
const styledOverlayWrapper = css`
  max-width: 350px;
`;

// #endregion CSS
